import * as React from "react";
import ReactDOM from "react-dom";
import { connect } from "react-redux";

import { translate } from "./../../../IntlProviderWrapper";
import { ValidLinesOrdersHelper } from "../reducers/ValidLinesOrdersHelper";
import { DraggableConsumer } from "../Draggable/DraggableContext";

import type { ReduxState } from "../reducers/reducers";
import type { DraggableProviderContext } from "../Draggable/DraggableContext";

export type OwnProps = {
    process_num: string;
    width: number;
    x: number;
    y: number;
    order_external_id: string;
    order_uuid: string;
    height: number;
};

type Props = OwnProps & ReduxProps;

type State = {
    selected_order_external_id: string | null;
    selected_order_uuid: string | null;
    computed_text_width: number;
};

class ProcessNumRect extends React.PureComponent<Props, State> {
    state: State = {
        selected_order_external_id: null,
        selected_order_uuid: null,
        computed_text_width: 0,
    };

    text_ref = React.createRef<SVGTextElement>();

    componentDidMount() {
        if (this.props.last_clicked_order_uuid) {
            this.load();
        }
    }

    componentDidUpdate() {
        if (
            this.props.last_clicked_order_uuid &&
            this.state.selected_order_uuid !== this.props.last_clicked_order_uuid
        ) {
            this.load();
        }
    }

    load() {
        const order_external_id = this.getOrderExternalIdOfSelectedOrder();
        this.setState(
            {
                selected_order_external_id: order_external_id,
                selected_order_uuid: this.props.last_clicked_order_uuid,
            },
            () => {
                if (this.text_ref && this.text_ref.current !== null) {
                    this.setState({
                        computed_text_width: this.text_ref.current.getComputedTextLength(),
                    });
                }
            }
        );
    }

    getOrderExternalIdOfSelectedOrder = () => {
        const operations = [
            ...ValidLinesOrdersHelper.getOperationsFromLineOrders(this.props.lines_orders),
            ...ValidLinesOrdersHelper.getOperationsFromLineOrders(this.props.unscheduled_orders),
        ];
        const selected_operation = operations.find(
            operation => operation.op_uuid === this.props.last_clicked_order_uuid
        );
        if (selected_operation) {
            return selected_operation.order_external_id;
        }
        return null;
    };

    renderProcessNumRect = () => {
        const order_width = this.props.width;
        if (order_width <= 0) {
            return null;
        }

        const order_uuid = this.props.order_uuid;
        const x = this.props.x + order_width;
        const y = this.props.y;
        const padding_x = 4;
        const width = this.state.computed_text_width + 2 * padding_x;
        const height = this.props.height / 2;
        const font_size = 11;
        const padding_y = (height - font_size) / 2;
        return (
            <g id="process-num-rect">
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    fill="white"
                    stroke="black"
                    strokeWidth="1"
                    rx={4}
                    style={{ userSelect: "none" }}
                >
                    <title>{translate("common.process_num", "Process num")}</title>
                </rect>
                <text
                    ref={this.text_ref}
                    fontSize={font_size}
                    fontWeight="500"
                    x={x + padding_x}
                    y={y + padding_y + 1}
                    style={{ userSelect: "none" }}
                    fill={"black"}
                    textAnchor="start"
                    alignmentBaseline="hanging"
                    // @ts-ignore
                    order_uuid={order_uuid}
                >
                    {this.props.process_num}
                </text>
            </g>
        );
    };

    render() {
        if (this.state.selected_order_external_id !== this.props.order_external_id) {
            return null;
        }

        const order_uuid = this.props.order_uuid;
        const process_nums_group = document.getElementById("gantt-chart-order-badges");

        return (
            <DraggableConsumer>
                {(value: DraggableProviderContext | null) => {
                    const dragging_order_uuid = value?.state?.dragging_order?.order_uuid ?? "";
                    const order_uuids = [order_uuid, ...(this.props.multiselect_order_uuids ?? [])];
                    if (process_nums_group === null || order_uuids.includes(dragging_order_uuid)) {
                        return this.renderProcessNumRect();
                    }

                    return ReactDOM.createPortal(this.renderProcessNumRect(), process_nums_group);
                }}
            </DraggableConsumer>
        );
    }
}

const mapState = (state: ReduxState) => {
    const last_clicked_order_uuid = state.gantt_chart_planning_table.last_clicked_order_uuid;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;
    const multiselect_order_uuids = state.gantt_chart_planning_table.multiselect_order_uuids;
    return {
        last_clicked_order_uuid,
        lines_orders,
        unscheduled_orders,
        multiselect_order_uuids,
    };
};

type ReduxProps = ReturnType<typeof mapState>;

export default connect<ReturnType<typeof mapState>, {}, OwnProps, ReduxState>(mapState)(ProcessNumRect);
